import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Button,
  Space,
  Input,
  Select,
  Typography,
  Tooltip,
  Avatar,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  EyeOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  FilterOutlined,
  MailOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useParams, useHistory } from "react-router-dom";
import { format } from "date-fns";
import { debounce } from "lodash";
import {
  getPulseReportById,
  getRecipients,
} from "@api/pulse-reports/pulseReport";
import { domainColors } from "./components";

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const PulseReportRecipients = () => {
  const { id } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [pulseReport, setPulseReport] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    search: "",
    opened: undefined,
  });

  const fetchPulseReport = async () => {
    try {
      const response = await getPulseReportById(id);
      setPulseReport(response.data);
    } catch (error) {
      console.error("Error fetching pulse report:", error);
      message.error("Failed to load pulse report");
    }
  };

  const fetchRecipients = async () => {
    try {
      setLoading(true);
      const { current, pageSize } = pagination;
      const { status, search, opened } = filters;

      const response = await getRecipients(id, current, pageSize, status, search, opened);
      setRecipients(response.data || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
      }));
    } catch (error) {
      console.error("Error fetching recipients:", error);
      message.error("Failed to load recipients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPulseReport();
  }, [id]);

  useEffect(() => {
    fetchRecipients();
  }, [id, pagination.current, pagination.pageSize, filters]);

  const debouncedSearch = debounce((value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => ({ ...prev, [filterType]: value }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      status: undefined,
      search: "",
      opened: undefined,
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTableChange = (newPagination) => {
    setPagination({
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      total: pagination.total,
    });
  };

  const handleRefresh = () => {
    fetchRecipients();
    message.success("Recipients refreshed");
  };

  const goBack = () => {
    history.push(`/super-admin/pulse-reports/${id}/stats`);
  };

  const extractDomain = (email) => {
    if (!email) return "other";
    const parts = email.split("@");
    return parts.length > 1 ? parts[1].split(".")[0].toLowerCase() : "other";
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      sent: { color: "success", icon: <CheckCircleOutlined />, label: "Sent" },
      delivered: { color: "success", icon: <CheckCircleOutlined />, label: "Delivered" },
      failed: { color: "error", icon: <CloseCircleOutlined />, label: "Failed" },
      bounced: { color: "warning", icon: <CloseCircleOutlined />, label: "Bounced" },
      pending: { color: "default", icon: <ClockCircleOutlined />, label: "Pending" },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.label}
      </Tag>
    );
  };

  const columns = [
    {
      title: "Recipient",
      key: "recipient",
      render: (_, record) => {
        const email = record.user?.email || record.email;
        const domain = extractDomain(email);
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              style={{
                backgroundColor: domainColors[domain] || domainColors.other,
                marginRight: 8,
              }}
              size="small"
              icon={<UserOutlined />}
            >
              {(record.user?.name?.charAt(0) || email?.charAt(0) || "?").toUpperCase()}
            </Avatar>
            <div>
              <div style={{ fontWeight: 500 }}>
                {record.user?.name || email}
              </div>
              {record.user?.name && (
                <div style={{ fontSize: 12, color: "#8c8c8c" }}>{email}</div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: "Engagement",
      key: "engagement",
      width: 180,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip
            title={
              <div>
                <div style={{ fontWeight: 500 }}>Opens</div>
                <p>First: {record.openedAt ? format(new Date(record.openedAt), "PPpp") : "Never"}</p>
                <p>Count: {record.openCount || 0}</p>
              </div>
            }
          >
            <Tag color={record.opened ? "green" : "default"}>
              <EyeOutlined /> {record.openCount || 0}
            </Tag>
          </Tooltip>
          <Tooltip
            title={
              <div>
                <div style={{ fontWeight: 500 }}>Clicks</div>
                <p>First: {record.clickedAt ? format(new Date(record.clickedAt), "PPpp") : "Never"}</p>
                <p>Count: {record.clickCount || 0}</p>
              </div>
            }
          >
            <Tag color={record.clicked ? "purple" : "default"}>
              <LinkOutlined /> {record.clickCount || 0}
            </Tag>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Plan",
      key: "plan",
      width: 120,
      render: (_, record) => (
        <Tag color="blue" style={{ textTransform: "capitalize" }}>
          {record.user?.planName || record.planName || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Domain",
      key: "domain",
      width: 100,
      render: (_, record) => {
        const email = record.user?.email || record.email;
        const domain = extractDomain(email);
        return (
          <Tag
            style={{
              backgroundColor: `${domainColors[domain] || domainColors.other}22`,
              color: domainColors[domain] || domainColors.other,
              border: `1px solid ${domainColors[domain] || domainColors.other}`,
            }}
          >
            {domain}
          </Tag>
        );
      },
    },
    {
      title: "Sent At",
      dataIndex: "sentAt",
      key: "sentAt",
      width: 180,
      render: (date) =>
        date ? format(new Date(date), "MMM dd, yyyy HH:mm") : "-",
      sorter: (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
    },
  ];

  return (
    <div style={{ padding: "0 24px" }}>
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={goBack}
            style={{ marginRight: 10 }}
          >
            Back to Stats
          </Button>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
            Refresh
          </Button>
        </Col>
      </Row>

      {/* Title */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          <MailOutlined style={{ marginRight: 8, color: "#1890ff" }} />
          {pulseReport?.name || "Pulse Report"} - Recipients
        </Title>
        <Text type="secondary">
          View all recipients and their engagement with this pulse report
        </Text>
      </Card>

      {/* Filters */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Status</div>
            <Select
              placeholder="Filter by status"
              style={{ width: "100%" }}
              value={filters.status}
              onChange={(value) => handleFilterChange("status", value)}
              allowClear
            >
              <Option value="sent">Sent</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="failed">Failed</Option>
              <Option value="bounced">Bounced</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Opened</div>
            <Select
              placeholder="Filter by opened"
              style={{ width: "100%" }}
              value={filters.opened}
              onChange={(value) => handleFilterChange("opened", value)}
              allowClear
            >
              <Option value={true}>Opened</Option>
              <Option value={false}>Not Opened</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Search</div>
            <Search
              placeholder="Search by email"
              onChange={(e) => debouncedSearch(e.target.value)}
              style={{ width: "100%" }}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Actions</div>
            <Button icon={<FilterOutlined />} onClick={clearFilters}>
              Clear Filters
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Recipients Table */}
      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={recipients}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} recipients`,
          }}
          onChange={handleTableChange}
          rowKey="_id"
          scroll={{ x: "max-content" }}
        />
      </Card>
    </div>
  );
};

export default PulseReportRecipients;
